import { useEffect, useState } from "react";
import { usePostHog } from 'posthog-js/react';
import { useProduct } from "@/hooks/api/useProducts";
import { useCollectionToggle } from '@/hooks/useCollectionToggle';
import { AnalyticsEvents } from '@/utils/analytics';
import { AddProductToCollectionHeader } from "@/components/products/add-to-collection/AddProductToCollectionHeader";
import { CollectionSelectorDrawer } from '@/components/shared/collection-selector/CollectionSelectorDrawer';

// A bottom sheet for adding a product to a collection
export const AddProductToCollectionBottomSheet = ({
  open,
  onClose,
  onNewProductClose,
  productId,
}: {
  open: boolean;
  onClose: () => void;
  onNewProductClose?: () => void;
  /** the ID of the product to add */
  productId: string;
}) => {
  const { data: product } = useProduct(productId);
  const { handleToggle } = useCollectionToggle(product);
  const posthog = usePostHog();
  const [searchQuery, setSearchQuery] = useState("");

  // Track when bottom sheet opens
  useEffect(() => {
    if (open && product) {
      posthog?.capture(AnalyticsEvents.SAVE_TO_COLLECTION_OPENED, {
        product_id: productId,
        product_name: product.name,
        brand: product.brand,
      });
    }
  }, [open, productId, product, posthog]);

  // Track search query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      posthog?.capture(AnalyticsEvents.SAVE_TO_COLLECTION_SEARCHED, {
        product_id: productId,
        search_query: searchQuery,
      });
    }
  }, [searchQuery, productId, posthog]);

  const handleClose = () => {
    onClose();
    onNewProductClose?.();
  };

  const handleCollectionToggle = (collectionId: string) => {
    handleToggle(collectionId);
  };

  const handleCreateCollection = () => {
    posthog?.capture(AnalyticsEvents.SAVE_TO_COLLECTION_NEW_CREATED, {
      product_id: productId,
      source: 'save_to_collection_flow',
    });
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const headerSlot = product ? (
    <AddProductToCollectionHeader
      name={product.name || "Loading..."}
      id={productId}
      images={product.images || []}
      brand={product.brand}
    />
  ) : null;

  return (
    <CollectionSelectorDrawer
      open={open}
      onClose={handleClose}
      productId={productId}
      headerSlot={headerSlot}
      onCollectionToggle={handleCollectionToggle}
      autoCloseOnSelect={true}
      onCreateCollection={handleCreateCollection}
      onSearchChange={handleSearchChange}
    />
  );
};
